import OpenViewerIcon from "@assets/icons/new/open-in-viewer.svg?react";
import ProjectsDuoColorIcon from "@assets/icons/projects-duo-color.svg?react";
import { useSphereDialog } from "@components/common/dialog/sphere-dialog-provider";
import { FaroTextButton } from "@components/common/faro-text-button";
import { PageContainer } from "@components/common/page-container";
import { ExternalLinks } from "@components/external-links";
import {
  SHARE_PROJECT_DIALOG_PROVIDER_PROPS,
  ShareProject,
} from "@components/project/share-project";
import { BaseCompanyIdProps } from "@custom-types/base-types";
import { SdbProject } from "@custom-types/project-types";
import { ProjectLaunchTarget } from "@custom-types/sdb-company-types";
import { blue, neutral, ShareLinkIcon } from "@flat-ui";
import { useHasUserValidFeatureCompanyLevel } from "@hooks/feature-control/use-has-user-valid-feature-company-level";
import { useAppNavigation } from "@hooks/navigation/use-app-navigation";
import { useMediaQueryList } from "@hooks/use-media-query";
import { Box, Divider, Stack, Typography } from "@mui/material";
import { CreateProjectDialog } from "@pages/projects/create-project-dialog";
import { ProjectTabs } from "@router/route-params";
import { generateProjectDetailsRoute } from "@router/router-helper";
import { DemoTag } from "@src/components/common/demo-tag";
import { EmptyPage } from "@src/components/common/empty-page/empty-page";
import { userMenuLink, withEllipsisTwoLines } from "@styles/common-styles";
import {
  defaultProjectThumbnail,
  getTrackProjectDateProps,
  openProjectInViewer,
  trackShareProject,
} from "@utils/project-utils";
import { ProjectEvents } from "@utils/track-event/track-event-list";
import { useTrackEvent } from "@utils/track-event/use-track-event";
import { useMemo } from "react";
import { Link } from "react-router-dom";

interface Props extends BaseCompanyIdProps {
  /** The demo project */
  demoProject: SdbProject;
}

/** Show this specific empty page when workspace has only the demo project (as active) */
export function ProjectsOverviewDemoEmptyPage({
  demoProject,
  companyId,
}: Props): JSX.Element {
  const { navigateToProjectDetail } = useAppNavigation();
  const { createDialog } = useSphereDialog();
  const { trackEvent, trackAsyncEvent } = useTrackEvent();
  const { isScreenMdOrLarger } = useMediaQueryList();
  const { canCreateProjects } = useHasUserValidFeatureCompanyLevel();

  /** Stores the link to open the details page of the demo project */
  const demoProjectLink = useMemo(() => {
    return (
      <Box
        component={Link}
        to={generateProjectDetailsRoute(
          companyId,
          demoProject.id,
          ProjectTabs.overview
        )}
        sx={{
          ...userMenuLink,
          color: blue[500],
        }}
        onClick={() => {
          trackEvent({
            name: ProjectEvents.openProjectDetails,
            props: {
              origin: "Empty projects page",
              type: "demo",
              projectId: demoProject.id,
              ...getTrackProjectDateProps(demoProject),
            },
          });
        }}
      >
        demo project
      </Box>
    );
  }, [companyId, demoProject, trackEvent]);

  return (
    <PageContainer
      title="Active Projects"
      description={
        <Typography
          sx={{
            color: neutral[600],
            fontSize: "12px",
          }}
        >
          Create, store and manage projects in your workspace. Projects can be
          organized into groups. <ExternalLinks link={"createNewProjectHelp"} />
        </Typography>
      }
    >
      <Stack
        sx={{
          width: "100%",
          mt: "15px",
          py: "10px",
          px: "20px",
          background: neutral[50],
          "&:hover": {
            background: neutral[100],
            "& .actionButtons": { visibility: "visible" },
          },
          cursor: "pointer",
        }}
        direction={{ xs: "column", md: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "flex-start", md: "center" }}
        onClick={() =>
          navigateToProjectDetail({ companyId, projectId: demoProject.id })
        }
      >
        <Stack direction="row">
          <Box
            component="img"
            src={demoProject.thumbnailUrl ?? defaultProjectThumbnail}
            sx={{
              maxHeight: "50px",
              maxWidth: "70px",
              objectFit: "contain",
              mx: "20px",
            }}
            alt="Project thumbnail"
          />

          <Box component="div">
            <Typography
              sx={{
                color: neutral[800],
                fontSize: "12px",
                fontWeight: "bold",
                mb: "7px",
              }}
            >
              Demo
            </Typography>
            <Typography
              sx={{
                color: neutral[600],
                fontSize: "12px",
                ...withEllipsisTwoLines,
              }}
            >
              Customise and explore our demo project
            </Typography>
          </Box>
        </Stack>
        <Stack
          direction="row"
          alignItems="center"
          sx={{
            width: !isScreenMdOrLarger ? "100%" : undefined,
            display: !isScreenMdOrLarger ? "flex" : undefined,
            justifyContent: !isScreenMdOrLarger ? "space-between" : undefined,
            marginTop: !isScreenMdOrLarger ? "5px" : undefined,
          }}
        >
          <Stack
            className="actionButtons"
            direction="row"
            alignItems="center"
            sx={{ visibility: isScreenMdOrLarger ? "hidden" : "visible" }}
          >
            {/* Shows a button to open the project, for all empty workspaces it will always use the Sphere Viewer. */}
            <FaroTextButton
              startIcon={
                <OpenViewerIcon style={{ width: "20px", height: "20px" }} />
              }
              hasOnlyIcon={!isScreenMdOrLarger}
              onClick={(event) => {
                // We need to preventDefault and stopPropagation to avoid having the default
                // click behavior for the card of opening project details.
                event.preventDefault();
                event.stopPropagation();

                const numberOfMembers = demoProject.members.length || undefined;

                void openProjectInViewer({
                  project: demoProject,
                  openFrom: "demo-project-empty-page",
                  numberOfMembers,
                  trackAsyncEvent,
                });
              }}
              sx={{ color: neutral[800] }}
            >
              {isScreenMdOrLarger ? "Open Project" : ""}
            </FaroTextButton>
            <Divider
              orientation="vertical"
              sx={{
                height: "20px",
                color: neutral[800],
                marginX: "16px",
              }}
            />

            {/* Shows a share button that always uses the Sphere Viewer for empty workspaces. */}
            <FaroTextButton
              startIcon={
                <ShareLinkIcon style={{ width: "20px", height: "20px" }} />
              }
              hasOnlyIcon={!isScreenMdOrLarger}
              onClick={(event) => {
                // We need to preventDefault and stopPropagation to avoid having the default
                // click behavior for the card of opening project details.
                event.preventDefault();
                event.stopPropagation();
                trackShareProject({
                  ...demoProject,
                  eventLocation: "no projects page",
                  trackEvent,
                });

                void createDialog(
                  SHARE_PROJECT_DIALOG_PROVIDER_PROPS,
                  <ShareProject
                    projectId={demoProject.id}
                    defaultOpenProjectTarget={ProjectLaunchTarget.sphereViewer}
                  />
                );
              }}
              sx={{ color: neutral[800], mr: "45px" }}
            >
              {isScreenMdOrLarger ? "Share Project" : ""}
            </FaroTextButton>
          </Stack>
          <DemoTag sx={{ mr: isScreenMdOrLarger ? "20px" : "0px" }} />
        </Stack>
      </Stack>
      <EmptyPage
        title={"You don't have any project yet"}
        subtitle={
          canCreateProjects ? (
            <>
              You haven't added projects to your workspace. Select the "new
              project" button to get started.
            </>
          ) : (
            <>
              You currently don't have any projects. To get started, explore and
              try out our {demoProjectLink}.
            </>
          )
        }
        icon={ProjectsDuoColorIcon}
        button={<CreateProjectDialog isDemo={true} />}
      />
    </PageContainer>
  );
}
